import type { QuizPlayer, QuizTeamMember } from './quiz-player';
import type { TeamEntryMethod } from '@lib/api/team-rating';
import type { ScreenshotMatch } from '@lib/quiz/screenshot-squad';
import type { TransferReplacementCardPlayer } from '@lib/api/transfer-replacements';
import type {
  ReadinessRaceState,
  ReadinessStakeKey,
  SanityReadinessRaceConfig,
  SanityReadinessScoringConfig,
  SanityReadinessVerdictState
} from '@lib/sanity/types';

export type ReadinessTeamMember = QuizTeamMember;

export type ReadinessTeamSource = 'screenshot' | 'fplId' | 'manual' | null;

export type ReadinessInputInternalStep =
  | 'import'
  | 'captain'
  | 'transfer'
  | 'miniLeague';

export type ReadinessOutputInternalStep = 'loading' | 'verdict';

export type CaptainComparisonResult = {
  userCaptain: QuizPlayer;
  aiCaptain: QuizPlayer;
  // Projected-points gain of the AI captain over the user's captain, supplied
  // by the server (team-rating captainPointsDelta). No per-player predictions
  // reach the client. Null when the server didn't supply a delta (e.g. the
  // re-rate failed), so consumers render a placeholder rather than a false 0.
  pointsDelta: number | null;
  isUserMatchingAi: boolean;
};

// The rank-1 card is the locked teaser: it has no player (the server never
// returns the IN recommendation), only its uplift. Ranks 2-3 carry identity.
// pointsUplift is relative to the out player, so no absolute predicted points
// reach the client.
export type TransferReplacementCard = {
  player: TransferReplacementCardPlayer | null;
  pointsUplift: number;
  isLocked: boolean;
};

export type MiniLeagueAnswer = {
  leagueType: string;
  leagueStakes: string;
};

export type VerdictData = {
  percentage: number;
  verdictLabel: string;
  verdictBandIndex: number;
};

// Step-flow types (pre-GW1 refactor)

export type ReadinessTeamMeta = {
  fplId: number | null;
  teamName: string | null;
  managerName: string | null;
  overallRank: number | null;
  /** GW points from team-rating performance; only present post-GW1 */
  gameweekPoints: number | null;
};

export type ReadinessTransferChoice =
  | { type: 'replacement'; index: number }
  | { type: 'roll' };

export type ReadinessCaptainOutcome = 'match' | 'mismatch' | 'skipped';

export type ReadinessTransferOutcome = 'selected' | 'roll' | 'undecided';

/**
 * What a screenshot import ended in.
 *
 * `needs-review` is a read the parser managed but the rules refuse: a squad
 * that is not 2 GK / 5 DEF / 5 MID / 3 FWD, a player read twice, or fewer than
 * fifteen found. It is not a failure: the read is good enough to put on a
 * pitch, so it routes to the review step rather than showing an error
 * (FFH-2738).
 */
export type ScreenshotImportStatus = 'imported' | 'needs-review' | 'error';

export type ReadinessVerdictResult = {
  score: number;
  winnersTarget: number;
  gap: number;
  oddsBandIndex: number;
  captainOutcome: ReadinessCaptainOutcome;
  transferOutcome: ReadinessTransferOutcome;
};

// Post-GW1 race types (FFH-1069)

/** One row from GET /fpl/entries/{id}/leagues. */
export type MiniLeagueSummary = {
  leagueId: number;
  name: string;
  userRank: number;
  /** null when the league paginates past the first standings page */
  leagueSize: number | null;
  userPoints: number;
  leaderPoints: number;
  /** rank 2's total; null for single-entry leagues */
  secondPoints: number | null;
};

export type RaceVerdictResult = {
  state: ReadinessRaceState;
  /**
   * Real FPL points: behind/level → points behind the leader (>= 0),
   * ahead → points clear of second place. Always 0 for lateStart.
   */
  gap: number;
  /** Projected gap at the projection gameweek; null for ahead/lateStart */
  projectedGap: number | null;
  userRank: number | null;
  leagueSize: number | null;
  leagueName: string | null;
};

export type ReadinessLiveScore = {
  score: number;
  winnersTarget: number;
};

export type ReadinessState = {
  team: ReadinessTeamMember[];
  teamSource: ReadinessTeamSource;
  /**
   * How the squad currently held in state was assembled, sent with the rating
   * request so invalid squads can be attributed to the picker or to screenshot
   * import. Null for an FPL-sourced squad, which the backend fetches itself.
   */
  entryMethod: TeamEntryMethod | null;
  fplId: string;
  // The AI's picks for the current squad, from the team-rating derived block.
  // Ids only; the funnel resolves them against `team` for display.
  aiCaptainId: string | null;
  aiSellPlayerId: string | null;
  selectedCaptain: QuizPlayer | null;
  captainComparison: CaptainComparisonResult | null;
  selectedTransferOut: QuizPlayer | null;
  selectedReplacementIndex: number | null;
  transferReplacements: TransferReplacementCard[] | null;
  isLoadingReplacements: boolean;
  replacementError: string | null;
  miniLeagueAnswer: MiniLeagueAnswer | null;
  verdictStates: SanityReadinessVerdictState[];
  verdict: VerdictData | null;
  isImporting: boolean;
  importError: string | null;
  /**
   * The last screenshot read, kept whole rather than reduced to ids, so the
   * review pitch can place the squad and name what it could not place. See the
   * matching field on GytrState.
   */
  screenshotMatches: ScreenshotMatch[] | null;
  /** Whether the review pitch is the step being shown. */
  isReviewingScreenshot: boolean;
  // Step-flow state (pre-GW1 refactor; unused by the legacy blocks)
  teamMeta: ReadinessTeamMeta | null;
  squadRating: number | null;
  leagueName: string | null;
  isLeagueNameSkipped: boolean;
  isCaptainSkipped: boolean;
  transferChoice: ReadinessTransferChoice | null;
  stakes: ReadinessStakeKey | null;
  // Partial: the Sanity document can be missing individual fields;
  // resolveScoringConfig merges it over the defaults at use time.
  scoringConfig: Partial<SanityReadinessScoringConfig> | null;
  readinessVerdict: ReadinessVerdictResult | null;
  // Post-GW1 race state (FFH-1069; unused pre-GW1)
  miniLeagues: MiniLeagueSummary[] | null;
  isLoadingLeagues: boolean;
  leaguesError: string | null;
  selectedLeague: MiniLeagueSummary | null;
  raceConfig: Partial<SanityReadinessRaceConfig> | null;
  raceVerdict: RaceVerdictResult | null;
  /**
   * The address the email gate captured.
   *
   * Held here because the provider is what reports the completion, and the
   * snapshot PATCH sends this as the fallback identity for a completion whose
   * `_funnel_session` cookie did not travel (see ffh-public-api FFH-2376).
   * Also the paywall's Auth0 login hint.
   */
  capturedEmail: string | null;
};

export type ReadinessActions = {
  importTeamFromScreenshot: (
    file: File,
    options?: { readErrorText?: string }
  ) => Promise<ScreenshotImportStatus>;
  /**
   * Rates the squad the visitor confirmed on the review pitch, in slot order.
   * Resolves false when the rating was refused, leaving them on the pitch with
   * the reason.
   */
  confirmTeamFromReview: (playerIds: string[]) => Promise<boolean>;
  setIsReviewingScreenshot: (isReviewing: boolean) => void;
  importTeamByFplId: (fplId: string) => Promise<void>;
  setTeamManually: (team: ReadinessTeamMember[]) => void;
  setCapturedEmail: (email: string) => void;
  selectCaptain: (player: QuizPlayer) => void;
  selectTransferOut: (player: QuizPlayer) => Promise<void>;
  selectReplacement: (index: number) => void;
  setMiniLeagueAnswer: (answer: MiniLeagueAnswer) => void;
  setVerdictStates: (states: SanityReadinessVerdictState[]) => void;
  resetForRetry: () => void;
  resetImportError: () => void;
  // Step-flow actions (pre-GW1 refactor)
  confirmTeamFromSearch: (entry: {
    fplEntryId: number;
    teamName: string;
    managerName: string;
  }) => Promise<boolean>;
  setTeamFromTemplate: (optaPlayerIds: string[]) => Promise<boolean>;
  setLeagueName: (name: string) => void;
  skipLeagueName: () => void;
  skipCaptain: () => void;
  setTransferChoice: (choice: ReadinessTransferChoice | null) => void;
  setStakes: (stakes: ReadinessStakeKey) => void;
  setScoringConfig: (
    config: Partial<SanityReadinessScoringConfig> | null
  ) => void;
  calculateReadinessVerdict: (
    oddsBands: { minGap: number; maxGap: number }[]
  ) => void;
  // Post-GW1 race actions (FFH-1069)
  fetchMiniLeagues: () => Promise<void>;
  selectLeague: (league: MiniLeagueSummary) => void;
  setRaceConfig: (config: Partial<SanityReadinessRaceConfig> | null) => void;
  calculateRaceVerdict: () => void;
};

export type ReadinessLiveRace = {
  userPercent: number;
  pacePercent: number;
};

export type ReadinessContextValue = {
  state: ReadinessState;
  actions: ReadinessActions;
  nextGameweek: number;
  isPreGw1: boolean;
  isPostGw1: boolean;
  liveReadiness: ReadinessLiveScore | null;
  liveRace: ReadinessLiveRace | null;
};

export const INITIAL_READINESS_STATE: ReadinessState = {
  team: [],
  teamSource: null,
  entryMethod: null,
  fplId: '',
  aiCaptainId: null,
  aiSellPlayerId: null,
  selectedCaptain: null,
  captainComparison: null,
  selectedTransferOut: null,
  selectedReplacementIndex: null,
  transferReplacements: null,
  miniLeagueAnswer: null,
  verdictStates: [],
  verdict: null,
  isLoadingReplacements: false,
  replacementError: null,
  isImporting: false,
  importError: null,
  screenshotMatches: null,
  isReviewingScreenshot: false,
  teamMeta: null,
  squadRating: null,
  leagueName: null,
  isLeagueNameSkipped: false,
  isCaptainSkipped: false,
  transferChoice: null,
  stakes: null,
  scoringConfig: null,
  readinessVerdict: null,
  miniLeagues: null,
  isLoadingLeagues: false,
  leaguesError: null,
  selectedLeague: null,
  raceConfig: null,
  raceVerdict: null,
  capturedEmail: null
};
